import type { CurrSection } from "./types";

/**
 * The action hash the canvas should highlight.
 *
 * Run History is not a separate panel -- it is itself an `activeItem`
 * (`{type:"history"}`), so selecting it would otherwise drop the border from
 * the action the user had selected. `prevActiveItem` is the selection History
 * was opened from (and the one Go back restores), so that is what stays
 * highlighted while History is open (STR-14148).
 */
export const canvasActiveActionHash = (
  activeItem: CurrSection,
  prevActiveItem: CurrSection,
): string | undefined => {
  const selection =
    activeItem?.type === "history" ? prevActiveItem : activeItem;

  return selection?.type === "action" ? selection.hash : undefined;
};

/**
 * The section the route-driven effect should switch to, or undefined to leave
 * the current selection alone.
 *
 * The trigger preselect only claims an EMPTY selection. It exists so opening
 * an existing automation with no deep-linked section shows the trigger's
 * settings instead of the "Choose a trigger" placeholder -- but the `:id`
 * route carries no section at all, so re-running it on every route change
 * also overwrote a selection the user had just made: clicking an action while
 * Run History was open set that action, navigated off the history route, and
 * was immediately reset to the trigger, so only a second click stuck
 * (STR-14148).
 */
export const sectionForRoute = (
  currSection: CurrSection | undefined,
  activeItem: CurrSection,
  isNewAutomation: boolean,
): CurrSection | undefined => {
  if (currSection !== undefined) {
    return currSection;
  }
  if (!isNewAutomation && activeItem == null) {
    return { type: "trigger", hash: "" };
  }
  return undefined;
};
